// src/components/Auth.jsx — GoldenScope login/signup screen
import { useState } from 'react';
import supabase from '../utils/supabaseClient';
import { CA_AGENCIES, AGENCIES_BY_CATEGORY, AGENCY_BY_NAME } from '../utils/caAgencies';

export default function Auth() {
  const [mode, setMode]       = useState('login'); // login | signup | forgot
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName]     = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorId, setSupervisorId] = useState('');
  const [selectedAgency, setSelectedAgency] = useState(null); // full agency object
  const [loadingSups, setLoadingSups] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  }

  function handleAgencySelect(agencyName) {
    setAgencyName(agencyName);
    const agency = AGENCY_BY_NAME[agencyName];
    setSelectedAgency(agency || null);
    if (agencyName) fetchSupervisors(agencyName);
  }

  async function fetchSupervisors(agencyNameVal) {
    if (!agencyNameVal.trim()) return;
    setLoadingSups(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, contact_name, team_name')
        .eq('agency_name', agencyNameVal.trim())
        .in('role', ['admin', 'agency_super_admin'])
        .eq('status', 'active');
      setSupervisors(data || []);
    } catch (e) { setSupervisors([]); }
    finally { setLoadingSups(false); }
  }

  async function handleSignup(e) {
    e.preventDefault();
    setLoading(true); setError('');
    if (!fullName.trim()) { setError('Full name is required'); setLoading(false); return; }
    if (!agencyName.trim()) { setError('Agency name is required'); setLoading(false); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }

    const { data, error: signupError } = await supabase.auth.signUp({ email, password,
      options: { data: { status: 'pending', role: 'member' } }
    });
    if (signupError) { setError(signupError.message); setLoading(false); return; }

    // Find or create agency — uses a SECURITY DEFINER RPC since this runs
    // before the new user has a user_profiles row, and is therefore subject
    // to RLS restrictions that a direct client-side select/insert would hit.
    const { data: agencyId, error: agencyError } = await supabase
      .rpc('find_or_create_agency', { p_name: agencyName.trim() });
    if (agencyError || !agencyId) {
      setError('Failed to find or create agency: ' + (agencyError?.message || 'unknown error'));
      setLoading(false); return;
    }

    // Determine role — first user at an agency becomes agency_super_admin.
    // Uses an RPC since a brand-new signup has no user_profiles row yet, and
    // the normal RLS-scoped count would always return 0 for them regardless
    // of how many members the agency actually already has.
    const { data: memberCount, error: countError } = await supabase
      .rpc('count_agency_members', { p_agency_id: agencyId });
    if (countError) {
      setError('Failed to check agency membership: ' + countError.message);
      setLoading(false); return;
    }
    const role = (memberCount === 0) ? 'agency_super_admin' : 'member';

    const { error: profileError } = await supabase.from('user_profiles').insert({
      id: data.user.id,
      agency_id: agencyId,
      role,
      contact_name: fullName.trim(),
      contact_email: email,
      agency_name: agencyName.trim(),
      supervisor_id: supervisorId || null,
      sco_agency_code: selectedAgency?.code || null,
      status: 'pending',
    });

    if (profileError) { setError('Failed to create profile: ' + profileError.message); setLoading(false); return; }

    setSuccess('Account created! Your request is pending approval. You will be notified when access is granted.');
    setMode('login');
    setLoading(false);
  }

  async function handleForgot(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://goldenscope.app',
    });
    if (error) setError(error.message);
    else setSuccess('Password reset link sent — check your email.');
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3260 60%, #0f1f3d 100%)',
      fontFamily: 'Inter, system-ui, sans-serif', padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo — clicks back to landing page */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <a href="/landing" style={{ textDecoration: 'none', display: 'inline-block', cursor: 'pointer' }}
            title="Back to GoldenScope home">
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 72, height: 72, borderRadius: 18,
              background: 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
              marginBottom: 16,
              padding: 12,
              boxShadow: '0 8px 32px rgba(196,153,34,0.4)',
              transition: 'transform .15s, box-shadow .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform='scale(1.06)'; e.currentTarget.style.boxShadow='0 12px 40px rgba(196,153,34,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 8px 32px rgba(196,153,34,0.4)'; }}
              dangerouslySetInnerHTML={{ __html: '<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 32 32\" width=\"100%\" height=\"100%\"><rect x=\"2\" y=\"13\" width=\"18\" height=\"6\" rx=\"3\" fill=\"url(#ab1)\"/><rect x=\"17\" y=\"14\" width=\"7\" height=\"4\" rx=\"2\" fill=\"url(#ab2)\"/><rect x=\"22\" y=\"14.5\" width=\"7\" height=\"3\" rx=\"1.5\" fill=\"url(#ab3)\"/><circle cx=\"4\" cy=\"16\" r=\"3.5\" fill=\"none\" stroke=\"#2A1200\" stroke-width=\"1.5\"/><circle cx=\"4\" cy=\"16\" r=\"2\" fill=\"url(#alens)\" opacity=\"0.85\"/><rect x=\"8\" y=\"13\" width=\"1.5\" height=\"6\" rx=\"0.5\" fill=\"url(#aband)\"/><rect x=\"14\" y=\"13\" width=\"1.5\" height=\"6\" rx=\"0.5\" fill=\"url(#aband)\"/><rect x=\"19\" y=\"14\" width=\"1\" height=\"4\" rx=\"0.4\" fill=\"url(#aband)\"/><line x1=\"24\" y1=\"14.8\" x2=\"24\" y2=\"17.2\" stroke=\"#1A0A00\" stroke-width=\"0.6\" opacity=\"0.5\"/><line x1=\"25.5\" y1=\"14.8\" x2=\"25.5\" y2=\"17.2\" stroke=\"#1A0A00\" stroke-width=\"0.6\" opacity=\"0.5\"/><defs><linearGradient id=\"ab1\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#6B3A00\"/><stop offset=\"40%\" stop-color=\"#3D2000\"/><stop offset=\"100%\" stop-color=\"#1A0A00\"/></linearGradient><linearGradient id=\"ab2\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#5A3000\"/><stop offset=\"50%\" stop-color=\"#3A1A00\"/><stop offset=\"100%\" stop-color=\"#1A0800\"/></linearGradient><linearGradient id=\"ab3\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#4A2800\"/><stop offset=\"100%\" stop-color=\"#150700\"/></linearGradient><linearGradient id=\"aband\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\"><stop offset=\"0%\" stop-color=\"#7A4500\"/><stop offset=\"100%\" stop-color=\"#2A1000\"/></linearGradient><radialGradient id=\"alens\" cx=\"35%\" cy=\"30%\"><stop offset=\"0%\" stop-color=\"#4A7AB5\"/><stop offset=\"100%\" stop-color=\"#0A1F3D\"/></radialGradient></defs></svg>' }} />
          </a>
          <a href="/landing" style={{ textDecoration: 'none', display: 'block' }}>
            <div style={{ color: '#fff', fontSize: 26, fontWeight: 800, letterSpacing: '-.5px', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.color='#e8c04a'}
              onMouseLeave={e => e.currentTarget.style.color='#fff'}>
              GoldenScope
            </div>
          </a>
          <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 13, marginTop: 4 }}>
            California State Contract Management
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: 36,
          boxShadow: '0 24px 64px rgba(0,0,0,.4)',
        }}>
          <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700, color: '#0f1f3d' }}>
            {mode === 'login' ? 'Sign in to your account'
              : mode === 'signup' ? 'Create your agency account'
              : 'Reset your password'}
          </h2>

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 16,
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#dc2626', fontSize: 13,
            }}>{error}</div>
          )}

          {success && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 16,
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              color: '#16a34a', fontSize: 13,
            }}>{success}</div>
          )}

          <form onSubmit={mode === 'login' ? handleLogin : mode === 'signup' ? handleSignup : handleForgot}>
            {mode === 'signup' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Full Name</label>
                  <input style={inputStyle} type="text" value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="e.g., Sean Grant"
                    required />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Agency</label>
                  <select style={{ ...inputStyle, color: agencyName ? '#0f1f3d' : '#94a3b8' }}
                    value={agencyName} onChange={e => handleAgencySelect(e.target.value)} required>
                    <option value="">-- Select your agency --</option>
                    {Object.entries(AGENCIES_BY_CATEGORY).map(([category, agencies]) => (
                      <optgroup key={category} label={category}>
                        {agencies.map(a => (
                          <option key={a.name} value={a.name}>
                            {a.name} ({a.abbr})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                    <option value="__other__">Other — not listed</option>
                  </select>
                  {agencyName === '__other__' && (
                    <input style={{ ...inputStyle, marginTop: 8 }} type="text"
                      onChange={e => setAgencyName(e.target.value)}
                      placeholder="Enter your agency name"
                      required />
                  )}
                  {selectedAgency && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginTop: 4 }}>
                      SCO Agency Code: {selectedAgency.code} · {selectedAgency.category}
                    </div>
                  )}
                </div>
                {supervisors.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Your Supervisor <span style={{ fontWeight: 400, opacity: .7 }}>(optional — select if known)</span></label>
                    <select style={{ ...inputStyle, color: supervisorId ? '#0f1f3d' : '#94a3b8' }}
                      value={supervisorId} onChange={e => setSupervisorId(e.target.value)}>
                      <option value="">-- Select your supervisor --</option>
                      {supervisors.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.contact_name}{s.team_name ? ' (' + s.team_name + ')' : ''}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginTop: 4 }}>
                      If your supervisor is not listed, leave blank — they can assign you later.
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Email Address</label>
              <input style={inputStyle} type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@agency.ca.gov" required />
            </div>

            {mode !== 'forgot' && (
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Password</label>
                <input style={inputStyle} type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                  required />
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
              background: loading ? '#d1b85a' : 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
              color: '#0f1f3d', fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 16px rgba(196,153,34,0.3)', transition: 'all .2s',
            }}>
              {loading ? 'Please wait…'
                : mode === 'login' ? 'Sign In'
                : mode === 'signup' ? 'Create Account'
                : 'Send Reset Link'}
            </button>
          </form>

          {/* Mode switchers */}
          <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#64748b' }}>
            {mode === 'login' && (<>
              <span>Don't have an account? </span>
              <button onClick={() => { setMode('signup'); setError(''); setSuccess(''); }} style={linkStyle}>
                Create one
              </button>
              <span style={{ margin: '0 8px' }}>·</span>
              <button onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} style={linkStyle}>
                Forgot password?
              </button>
            </>)}
            {mode === 'signup' && (<>
              <span>Already have an account? </span>
              <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={linkStyle}>
                Sign in
              </button>
            </>)}
            {mode === 'forgot' && (
              <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={linkStyle}>
                Back to sign in
              </button>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, color: 'rgba(255,255,255,.3)', fontSize: 12 }}>
          Powered by Penny · GoldenScope © 2025
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: '#374151', marginBottom: 6,
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #e2e8f0', fontSize: 14, outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
  transition: 'border-color .15s',
};

const linkStyle = {
  background: 'none', border: 'none', color: '#c49922',
  cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0,
};
